import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const RoiMarketingCampaigns = () => {
  // State variables for ROI calculation
  const [revenue, setRevenue] = useState(0); // Revenue from marketing campaign
  const [marketingCost, setMarketingCost] = useState(0); // Cost of the marketing campaign
  const [roi, setRoi] = useState(0); // Calculated ROI

  // Event handlers to update state values
  const handleRevenueChange = (e) =>
    setRevenue(parseFloat(e.target.value) || 0);
  const handleMarketingCostChange = (e) =>
    setMarketingCost(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the ROI
  useEffect(() => {
    if (marketingCost > 0) {
      // Formula: ROI (%) = [(Revenue - Marketing Cost) / Marketing Cost] x 100
      const roiValue = ((revenue - marketingCost) / marketingCost) * 100;
      setRoi(roiValue);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [revenue, marketingCost]);

  return (
    <Layout title="ROI for Marketing Campaigns" path="roi-calculator">
      <section className="section-padding" id="roi-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* ROI Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  ROI for Marketing Campaigns
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Revenue */}
                    <div className="col-md-7 col-lg-5">
                      <label htmlFor="revenue" className="form-label">
                        Revenue from Marketing Campaign
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Revenue:</strong> The total revenue generated directly from the marketing campaign."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="revenue"
                        placeholder="Enter revenue"
                        value={revenue || ""}
                        onChange={handleRevenueChange}
                      />
                    </div>

                    {/* Input for Marketing Cost */}
                    <div className="col-md-5 col-lg-4">
                      <label htmlFor="marketingCost" className="form-label">
                        Marketing Cost
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Marketing Cost:</strong> The total cost incurred for the marketing campaign."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="marketingCost"
                        placeholder="Enter marketing cost"
                        value={marketingCost || ""}
                        onChange={handleMarketingCostChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        ROI:{" "}
                        <span className="fw-bold ms-3">
                          {roi === 0
                            ? "0"
                            : roi.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          {" %"}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to ROI for Marketing Campaigns
                </h3>

                {/* What is ROI for Marketing Campaigns? */}
                <h4 className="mt-3 mb-3">
                  What is ROI for Marketing Campaigns?
                </h4>
                <p>
                  **ROI (Return on Investment)** for marketing campaigns is a
                  performance metric used to evaluate the efficiency and
                  profitability of a marketing investment. It calculates the
                  return achieved relative to the cost of the campaign, allowing
                  businesses to assess the effectiveness of their marketing
                  strategies.
                </p>

                {/* Why Use the ROI Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the ROI Calculator?</h4>
                <p>
                  The ROI calculator helps businesses measure the profitability
                  of their marketing campaigns. By knowing how much revenue a
                  campaign generates in comparison to the marketing expenses,
                  businesses can make more informed decisions about where to
                  allocate their marketing budget.
                </p>

                {/* Benefits of Using the ROI Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the ROI Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps Measure the Effectiveness of Marketing Efforts:
                    </strong>{" "}
                    Provides insights into whether a campaign is generating a
                    positive return.
                  </li>
                  <li>
                    <strong>
                      Supports Budget Allocation and Campaign Planning:
                    </strong>{" "}
                    Assists in deciding how much to invest in future marketing
                    campaigns based on past performance.
                  </li>
                  <li>
                    <strong>
                      Useful for Performance Tracking and Decision Making:
                    </strong>{" "}
                    Helps track campaign performance and supports strategic
                    marketing decisions.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the ROI Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Marketing Teams:</strong> Helps marketing teams
                    assess and optimize their campaigns.
                  </li>
                  <li>
                    <strong>Agencies:</strong> Useful for agencies to report
                    campaign success to clients and make future campaign
                    recommendations.
                  </li>
                  <li>
                    <strong>Business Owners:</strong> Assists business owners in
                    evaluating the profitability of their marketing investments.
                  </li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How can I improve my ROI for marketing campaigns?
                </h4>
                <p>
                  Improving ROI can involve targeting the right audience,
                  optimizing ad spend, refining content strategies, or
                  leveraging more cost-effective marketing channels.
                </p>

                <h4 className="mt-4 mb-3">
                  What is a good ROI for marketing campaigns?
                </h4>
                <p>
                  A good ROI can vary by industry, but typically a ROI greater
                  than 100% indicates that the marketing campaign has been
                  profitable. The higher the ROI, the better the campaign's
                  performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RoiMarketingCampaigns;
